/**
 * Close / Dismiss (README §5; §10 "Close"). One state machine, two labels,
 * decided by ticket-link state at the moment of close (idea #4):
 *
 * - Linked: an AI-drafted, editable close note plus a client-facing message
 *   choice (including "nothing sent to the client"). The draft comes from
 *   `modalReads.closeDraft` — a read seam, since Phase 1's operations layer
 *   has no close-note-drafting endpoint yet (n8n + Claude, a later phase).
 *   Per the brief, this component never fabricates a draft: when the seam
 *   answers unwired (today, always — see ./reads.ts), the textarea shows an
 *   honest "no draft available" placeholder and starts editable-empty rather
 *   than pretending to have read the thread. It is never left un-rendered,
 *   though — a linked thread always gets the field, worked from real data
 *   the moment the seam is wired.
 * - Unlinked: pure disposal. No note field, no client-message choice — there
 *   is nothing to draft and nothing will be written to Halo (enforced
 *   server-side by `enqueueIfLinked`'s `ticket === null` check in
 *   services/ops/src/routes/operations.ts; this modal simply never offers UI
 *   for fields that would have no effect).
 *
 * Both paths call the same `close-thread` operation — the difference is
 * only which arguments this modal ever populates. Idea #4: the copy states
 * close is terminal, because it is (§10: "A closed thread never reopens").
 */
import { useEffect, useState } from 'react'
import type { ThreadSummary } from '@communicator/shared'
import { Button, Modal } from '../ui'
import { ops, OpsError } from '../../lib/ops'
import { useStore } from '../../store'
import { modalReads } from './reads'
import { CLOSE_CUE_RESOLVED, CLOSE_CUE_RESOLVED_TEXT, CLOSE_TERMINAL_TEXT, CLOSE_UNLINKED_TEXT, GENERIC_OP_ERROR } from './format'

export function CloseModal() {
  const modal = useStore((s) => s.modal)
  const activeId = useStore((s) => s.activeId)
  const threads = useStore((s) => s.threads)
  const closeNote = useStore((s) => s.closeNote)
  const closeCue = useStore((s) => s.closeCue)
  const set = useStore((s) => s.set)
  const closeModal = useStore((s) => s.closeModal)
  const removeThread = useStore((s) => s.removeThread)
  const selectThread = useStore((s) => s.selectThread)

  const open = modal === 'close'
  const thread = threads.find((t) => t.id === activeId) ?? null
  const linked = !!thread?.ticket

  const [draftReady, setDraftReady] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)

  useEffect(() => {
    if (!open || !activeId || !linked) return
    let cancelled = false
    modalReads.closeDraft(activeId).then((d) => {
      if (cancelled) return
      if (d.wired) set({ closeNote: d.note })
      setDraftReady(true)
    })
    return () => {
      cancelled = true
    }
  }, [open, activeId, linked, set])

  useEffect(() => {
    if (!open) {
      setError(null)
      setBusy(false)
      setDraftReady(false)
    }
  }, [open])

  if (!open) return null

  async function handleConfirm() {
    if (!activeId) return
    setBusy(true)
    setError(null)
    try {
      const args = linked
        ? {
            threadId: activeId,
            note: closeNote.trim().length > 0 ? closeNote : undefined,
            clientMessage: closeCue === CLOSE_CUE_RESOLVED ? CLOSE_CUE_RESOLVED_TEXT : undefined,
          }
        : { threadId: activeId }
      const result = (await ops.call('close-thread', args)) as { thread: ThreadSummary }
      removeThread(result.thread.id)
      set({ closeNote: '', closeCue: null })
      closeModal()
      const remaining = useStore.getState().threads
      selectThread(remaining[0]?.id ?? null)
    } catch (e) {
      setError(e instanceof OpsError ? e.message : GENERIC_OP_ERROR)
    } finally {
      setBusy(false)
    }
  }

  return (
    <Modal
      open={open}
      title={linked ? 'Close' : 'Dismiss'}
      onClose={closeModal}
      footer={
        <div className="flex justify-end gap-2">
          <Button variant="secondary" onClick={closeModal}>
            Cancel
          </Button>
          <Button data-testid="close-modal-confirm-btn" disabled={busy} onClick={handleConfirm}>
            {linked ? 'Close thread' : 'Dismiss'}
          </Button>
        </div>
      }
    >
      <p data-testid="close-modal-terminal-copy" className="mb-3 text-[13px] text-mid">
        {CLOSE_TERMINAL_TEXT}
      </p>

      {error && (
        <p
          data-testid="close-modal-error"
          role="alert"
          className="mb-3 rounded-[8px] bg-bad-subtle px-3 py-2 text-[12px] text-bad"
        >
          {error}
        </p>
      )}

      {linked ? (
        <div data-testid="close-modal-linked-body">
          <p className="mb-1 text-[11px] font-semibold uppercase text-lo">Close note</p>
          <textarea
            data-testid="close-modal-note-textarea"
            className="mb-3 h-[96px] w-full rounded-[8px] border border-subtle bg-card px-2.5 py-2 text-[13px] text-hi"
            placeholder={draftReady ? 'No AI draft available — write a closing note.' : 'Drafting a close note…'}
            value={closeNote}
            onChange={(e) => set({ closeNote: e.target.value })}
          />
          <p className="mb-1 text-[11px] font-semibold uppercase text-lo">Client hears</p>
          <div className="flex flex-col gap-1.5">
            <label className="flex items-center gap-2 text-[13px] text-mid">
              <input
                type="radio"
                name="close-cue"
                data-testid="close-modal-cue-none"
                checked={closeCue !== CLOSE_CUE_RESOLVED}
                onChange={() => set({ closeCue: null })}
              />
              Nothing sent to the client
            </label>
            <label className="flex items-center gap-2 text-[13px] text-mid">
              <input
                type="radio"
                name="close-cue"
                data-testid="close-modal-cue-resolved"
                checked={closeCue === CLOSE_CUE_RESOLVED}
                onChange={() => set({ closeCue: CLOSE_CUE_RESOLVED })}
              />
              "{CLOSE_CUE_RESOLVED_TEXT}"
            </label>
          </div>
        </div>
      ) : (
        <p data-testid="close-modal-unlinked-copy" className="text-[13px] text-mid">
          {CLOSE_UNLINKED_TEXT}
        </p>
      )}
    </Modal>
  )
}
